import React, { useEffect } from 'react'
import {
  Table, Button, Space, Card, Form, Input, InputNumber, Select, Col, message, Drawer, Tooltip, Popconfirm, Alert,
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, NumberOutlined, CheckCircleOutlined, SearchOutlined, SyncOutlined,
} from '@ant-design/icons'
import { useMasterData, type CodeMaster } from '@/context/MasterDataContext'
import { useMasterFormRoute } from '@/hooks/useMasterFormRoute'
import MasterFormShell from '@/components/MasterFormShell'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import FilterBarCard from '@/components/FilterBarCard'
import { genId } from '@/utils/genId'
import { api } from '@/services/api'

const BASE = '/master/code-masters'

const MONO: React.CSSProperties = { fontFamily: 'JetBrains Mono, monospace' }

// The master modules a numbering series can drive today — moduleKey must
// match the internal table/resource key used elsewhere (resourceRoutes.js /
// userService.js), since that's the string generateCode() passes to
// POST /code-masters/generate/:moduleKey.
const KNOWN_MODULES = [
  // Core master records
  { key: 'users',               label: 'Employee' },
  { key: 'generators',          label: 'Generator' },
  { key: 'consumers',           label: 'Consumer' },
  { key: 'roles',               label: 'Role' },
  // Lookup / reference tables
  { key: 'power_types',         label: 'Power Type' },
  { key: 'supply_modes',        label: 'Supply Mode' },
  { key: 'agreement_types',     label: 'Agreement Type' },
  { key: 'edcs',                label: 'EDC' },
  { key: 'drawal_voltages',     label: 'Drawal Voltage' },
  { key: 'injecting_voltages',  label: 'Injecting Voltage' },
  { key: 'slots',               label: 'Slot' },
  { key: 'tneb_categories',     label: 'TNEB Category' },
  { key: 'gst_configs',         label: 'GST Config' },
  // Tariff / pricing
  { key: 'tariffs',             label: 'Shakthi Power Tariff' },
  { key: 'tneb_tariffs',        label: 'TNEB Tariff' },
  // CRM & Sales
  { key: 'leads',               label: 'Lead' },
  { key: 'proposal_ids',        label: 'Proposal' },
  { key: 'agreements',          label: 'Agreement' },
  // Operations / Finance
  { key: 'invoices',            label: 'Invoice' },
  { key: 'payouts',             label: 'Payout' },
  { key: 'payments',            label: 'Payment Receipt' },
  { key: 'allocations',         label: 'Unit Allocation' },
  { key: 'meter_readings',      label: 'Meter Reading' },
  // Templates
  { key: 'proposal_templates',  label: 'Proposal Template' },
  { key: 'email_templates',     label: 'Email Template' },
  { key: 'whatsapp_templates',  label: 'WhatsApp Template' },
]

const previewCode = (row: Pick<CodeMaster, 'prefix' | 'padding' | 'nextNumber' | 'suffix'>): string =>
  `${row.prefix || ''}${String(row.nextNumber ?? 1).padStart(row.padding || 3, '0')}${row.suffix || ''}`

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 12 }}>
    <div style={{ width: 140, flexShrink: 0, fontSize: 12, color: '#8A93A6', fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 13, color: '#0B1426', flex: 1 }}>{value ?? <span style={{ color: '#ccc' }}>—</span>}</div>
  </div>
)

const CodeMasterPage: React.FC = () => {
  const { codeMasters, deleteCodeMaster, saveCodeMaster, masterDataLoaded } = useMasterData()
  const { mode, editId, isForm, goAdd, goEdit, goList } = useMasterFormRoute(BASE)
  const [viewRecord, setViewRecord] = React.useState<CodeMaster | null>(null)
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = React.useState(false)

  /* List search / filter */
  const [searchText, setSearchText] = React.useState('')
  const [filterStatus, setFilterStatus] = React.useState<string>('')

  const activeCount = codeMasters.filter(c => c.status === 'active').length
  const usedModuleKeys = new Set(codeMasters.map(c => c.moduleKey))
  const availableModules = KNOWN_MODULES.filter(m => !usedModuleKeys.has(m.key))

  const filteredCodeMasters = codeMasters.filter(c => {
    const q = searchText.toLowerCase()
    const matchSearch = !q ||
      (c.moduleLabel || '').toLowerCase().includes(q) ||
      (c.moduleKey || '').toLowerCase().includes(q) ||
      (c.prefix || '').toLowerCase().includes(q)
    const matchStatus = !filterStatus || c.status === filterStatus
    return matchSearch && matchStatus
  })

  // Populate the form when entering the full-page add/edit route
  useEffect(() => {
    if (mode === 'new') {
      form.resetFields()
      form.setFieldsValue({ padding: 3, nextNumber: 1, status: 'active' })
    } else if (mode === 'edit') {
      const rec = codeMasters.find(c => c.id === editId)
      if (rec) form.setFieldsValue(rec)
      else goList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, editId, codeMasters])

  const handleDelete = async (id: string) => {
    try {
      await deleteCodeMaster(id)
      message.success('Removed')
      if (isForm) goList()
    } catch { /* error already shown */ }
  }

  const onFinish = async (values: any) => {
    const isEdit = mode === 'edit'
    const moduleInfo = KNOWN_MODULES.find(m => m.key === values.moduleKey)
    const payload = {
      ...values,
      moduleLabel: values.moduleLabel || moduleInfo?.label || values.moduleKey,
      id: isEdit ? editId : genId('CM'),
    }
    setSubmitting(true)
    try {
      await saveCodeMaster(payload, isEdit)
      message.success(isEdit ? 'Code series updated' : 'Code series added')
      goList()
    } catch { /* error already shown; stay on the form */ }
    finally { setSubmitting(false) }
  }

  const columns = [
    {
      title: 'Module', dataIndex: 'moduleLabel', width: 180,
      render: (v: string, r: CodeMaster) => (
        <div>
          <span style={{ fontWeight: 600 }}>{v}</span>
          <div style={{ fontSize: 11, color: '#94a3b8', ...MONO }}>{r.moduleKey}</div>
        </div>
      ),
    },
    {
      title: 'Next Code', width: 150,
      render: (_: any, r: CodeMaster) => (
        <span style={{ ...MONO, fontWeight: 700, fontSize: 14, color: '#1677ff' }}>{previewCode(r)}</span>
      ),
    },
    { title: 'Prefix', dataIndex: 'prefix', width: 90, render: (v: string) => <span style={MONO}>{v || '—'}</span> },
    { title: 'Padding', dataIndex: 'padding', width: 90 },
    { title: 'Next Number', dataIndex: 'nextNumber', width: 110 },
    {
      title: 'Status', dataIndex: 'status', width: 110,
      render: (s: string) => (
        <span className={`pill ${s === 'active' ? 'pill-green' : 'pill-neutral'}`}>
          {s === 'active' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: CodeMaster) => (
        <Space size="small" onClick={e => e.stopPropagation()}>
          <Tooltip title="View">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setViewRecord(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goEdit(record.id)} />
          </Tooltip>
          <Popconfirm
            title="Delete this code series?"
            description="New records in this module will fail to save until a series is configured again."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="link" danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  // ── Full-page Add / Edit form ──────────────────────────────────
  if (isForm) {
    const editingRecord = mode === 'edit' ? codeMasters.find(c => c.id === editId) : undefined
    return (
      <MasterFormShell
        title={mode === 'edit' ? `Edit Code Series — ${editingRecord?.moduleLabel || ''}` : 'Add Code Series'}
        subtitle="Auto-numbering rule for a master module's code — prefix, padding and the next running number"
        onBack={goList}
        extra={mode === 'edit' && editId ? (
          <Popconfirm
            title="Delete this code series?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(editId)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        ) : undefined}
      >
        <Form form={form} layout="vertical" onFinish={onFinish} style={{ maxWidth: 640 }}>
          <Form.Item name="moduleKey" label="Module" rules={[{ required: true, message: 'Please select a module' }]}>
            <Select
              disabled={mode === 'edit'}
              placeholder="Select a master module"
              options={KNOWN_MODULES.map(m => ({
                value: m.key,
                label: mode === 'new' && usedModuleKeys.has(m.key) ? `${m.label} (already configured)` : m.label,
                disabled: mode === 'new' && usedModuleKeys.has(m.key),
              }))}
              onChange={(key) => {
                const m = KNOWN_MODULES.find(x => x.key === key)
                if (m) form.setFieldsValue({ moduleLabel: m.label })
              }}
            />
          </Form.Item>
          <Form.Item name="moduleLabel" label="Display Name" rules={[{ required: true, message: 'Please input a display name' }]}>
            <Input placeholder="e.g. Employee" />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="prefix" label="Prefix">
              <Input placeholder="e.g. EMP" style={{ ...MONO, fontWeight: 700 }} />
            </Form.Item>
            <Form.Item name="padding" label="Zero-padding" rules={[{ required: true }]} initialValue={3}>
              <InputNumber min={1} max={6} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="suffix" label="Suffix (optional)">
              <Input style={{ ...MONO, fontWeight: 700 }} />
            </Form.Item>
          </div>
          <Form.Item
            name="nextNumber"
            label="Next Number"
            rules={[{ required: true }]}
            initialValue={1}
            extra="The number the next new record in this module will receive. Raise it to skip ahead, or set it back to 1 to restart the series."
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="active">
            <Select options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
          </Form.Item>

          <Form.Item shouldUpdate style={{ marginBottom: 0 }}>
            {() => {
              const vals = form.getFieldsValue(['prefix', 'padding', 'nextNumber', 'suffix'])
              return (
                <Alert
                  type="info"
                  showIcon
                  message={<span>Next code generated will be: <span style={{ ...MONO, fontWeight: 700 }}>{previewCode(vals)}</span></span>}
                  style={{ marginBottom: 16, borderRadius: 8 }}
                />
              )
            }}
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Button onClick={goList} disabled={submitting}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>{mode === 'edit' ? 'Update Series' : 'Add Series'}</Button>
          </div>
        </Form>
      </MasterFormShell>
    )
  }

  // ── List ───────────────────────────────────────────────────────
  if (!masterDataLoaded) return <TableSkeleton columns={6} />

  const handleResetAllTo1 = async () => {
    try {
      try {
        await api.post('/code-masters/reset-all', {})
      } catch {
        await Promise.all(
          codeMasters.map(c => saveCodeMaster({ ...c, nextNumber: 1 }, true))
        )
      }
      message.success('All code series reset to start from 001')
      window.location.reload()
    } catch (err: any) {
      message.error(err.message || 'Failed to reset code series')
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <NumberOutlined style={{ color: '#001A4D' }} /> Code Master
          </h2>
          <p className="page-subtitle">
            Configure the auto-numbering rule for each master module's code — new records
            (e.g. a new Employee) get their code generated from here starting from 001.
          </p>
        </div>
        <Space>
          <Popconfirm
            title="Reset all code series starting number to 1?"
            description="Next code for all modules will start from 001 (e.g. WAT001, GSPEMP001, TNEB001)."
            onConfirm={handleResetAllTo1}
            okText="Reset All to 1"
            cancelText="Cancel"
          >
            <Button icon={<SyncOutlined />}>
              Reset All to 001
            </Button>
          </Popconfirm>
          <Button type="primary" icon={<PlusOutlined />} onClick={goAdd} disabled={!availableModules.length}>
            Add Series
          </Button>
        </Space>
      </div>

      {/* ── Stat cards ── */}
      <KpiCardRow
        cards={[
          { label: 'Total Series', value: codeMasters.length, icon: <NumberOutlined style={{ fontSize: 24, color: '#001A4D' }} />, iconBg: 'rgba(0,26,77,0.08)', borderColor: '#001A4D' },
          { label: 'Active', value: activeCount, icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#059669' }} />, iconBg: 'rgba(5,150,105,0.08)', borderColor: '#059669' },
        ]}
      />

      {/* ── Filter bar ── */}
      <FilterBarCard>
        <Col xs={24} sm={12} lg={10}>
          <Input.Search
            placeholder="Search by module, key or prefix..."
            allowClear
            prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(v) => setSearchText(v)}
          />
        </Col>
        <Col xs={24} sm={12} lg={7}>
          <Select
            placeholder="Filter by Status"
            allowClear
            value={filterStatus || undefined}
            onChange={(v) => setFilterStatus(v || '')}
            style={{ width: '100%' }}
            options={[{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }]}
          />
        </Col>
      </FilterBarCard>

      <Card>
        <Table
          dataSource={filteredCodeMasters}
          columns={columns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (t: number) => `${t} series` }}
        />
      </Card>

      {/* ── Detail Drawer ── */}
      <Drawer
        title={
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{viewRecord?.moduleLabel}</div>
            <div style={{ fontSize: 12, color: '#8A93A6', ...MONO }}>{viewRecord?.moduleKey}</div>
          </div>
        }
        open={!!viewRecord}
        onClose={() => setViewRecord(null)}
        width={400}
        extra={
          <Space>
            <Tooltip title="Edit">
              <Button type="text" icon={<EditOutlined />} onClick={() => { if (viewRecord) goEdit(viewRecord.id); setViewRecord(null) }} />
            </Tooltip>
            <Tooltip title="Delete">
              <Popconfirm
                title="Delete this code series?"
                description="This action cannot be undone."
                onConfirm={() => { handleDelete(viewRecord!.id); setViewRecord(null) }}
                okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          </Space>
        }
      >
        {viewRecord && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <span className={`pill ${viewRecord.status === 'active' ? 'pill-green' : 'pill-neutral'}`}>
                {viewRecord.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
            <DetailRow label="Next Code" value={<span style={{ ...MONO, fontWeight: 700 }}>{previewCode(viewRecord)}</span>} />
            <DetailRow label="Prefix" value={<span style={MONO}>{viewRecord.prefix || '—'}</span>} />
            <DetailRow label="Zero-padding" value={viewRecord.padding} />
            <DetailRow label="Next Number" value={viewRecord.nextNumber} />
            <DetailRow label="Suffix" value={viewRecord.suffix ? <span style={MONO}>{viewRecord.suffix}</span> : undefined} />
          </div>
        )}
      </Drawer>
    </div>
  )
}

export default CodeMasterPage
